import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { BASE_URL } from "../store/urls";
import { CREATE_RESTAURANT } from "../apollo/Mutations";
import { GET_ACTIVE_RESTAURANTS } from "../apollo/Queries";


export default function AddRestaurantModal({ isOpen, onClose, initialData }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  const [uploading, setUploading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [createRestaurant, { loading }] = useMutation(CREATE_RESTAURANT, {
    refetchQueries: [{ query: GET_ACTIVE_RESTAURANTS, variables: { name: "", myRestaurants: false } }]
  });

  useEffect(() => {
    if (initialData) {
      Object.keys(initialData).forEach(key => {
        if (key === 'openingDays' && Array.isArray(initialData[key])) {
          setValue(key, initialData[key].join(', '));
        } else {
          setValue(key, initialData[key]);
        }
      });
    } else {
      reset();
    }
  }, [initialData, setValue, reset, isOpen]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const response = await fetch(`${BASE_URL}/files/upload/image`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      console.log("Upload result:", result);
      setValue("coverPhoto", result.data); // Set uploaded file URL
    } catch (err) {
      console.error("File upload failed:", err);
      alert("Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const submitHandler = async (formData) => {
    try {
      const variables = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        openingDays: typeof formData.openingDays === 'string'
          ? formData.openingDays.split(',').map(s => s.trim())
          : formData.openingDays,
        uuid: initialData?.uuid || ""
      };
      const { data } = await createRestaurant({ variables });
      if (data?.createRestaurant?.error) {
        setResponseMsg(`❌ ${data.createRestaurant.message}`);
      } else {
        setResponseMsg(`✅ ${data.createRestaurant.message}`);
        setTimeout(() => {
          setResponseMsg("");
          reset();
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("GraphQL error:", error);
      setResponseMsg("❌ Failed to save restaurant.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl p-8 rounded-[2rem] shadow-2xl relative animate-fade-in">
        <h2 className="text-2xl font-black text-premium-teal mb-6">
          {initialData ? "Update Restaurant Details" : "Register Restaurant"}
        </h2>

        <form onSubmit={handleSubmit(submitHandler)} className="space-y-6 overflow-y-auto max-h-[75vh] pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Restaurant Name</label>
              <input {...register("name", { required: "Name is required" })} placeholder="e.g. The Grand Bistro" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
              {errors.name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1">{errors.name.message}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Mail Address</label>
              <input {...register("email", { required: "Email is required" })} placeholder="contact@hub.com" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
              {errors.email && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1">{errors.email.message}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Primary Phone</label>
              <input {...register("phone", { required: "Phone is required" })} placeholder="+1 234 567 890" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Physical Address</label>
              <input {...register("address", { required: "Address is required" })} placeholder="123 Luxury Ave, City" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Latitude</label>
              <input {...register("latitude", { required: true })} placeholder="1.2345" type="number" step="any" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Longitude</label>
              <input {...register("longitude", { required: true })} placeholder="103.4567" type="number" step="any" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Opening Hour</label>
              <input {...register("openTime", { required: true })} type="time" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Closing Hour</label>
              <input {...register("closingTime", { required: true })} type="time" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Operating Days</label>
            <input {...register("openingDays", { required: true })} placeholder="Monday, Tuesday, Wednesday..." className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Description</label>
            <textarea {...register("description", { required: true })} placeholder="Describe the essence of this hub..." rows="3" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all resize-none" />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Cover Photo</label>
            <div className="flex items-center gap-4">
              <input type="file" accept="image/*" onChange={handleFileChange} className="text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-premium-gold/10 file:text-premium-gold hover:file:bg-premium-gold/20 transition-all cursor-pointer" />
              {uploading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-premium-gold/20 border-t-premium-gold"></div>}
            </div>
            <input type="hidden" {...register("coverPhoto", { required: !initialData })} />
            {errors.coverPhoto && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1">Cover photo is essential.</p>}
          </div>

          <div className="pt-4 flex items-center justify-between">
            <div className="flex-1">
              {responseMsg && (
                <p className={`text-[10px] font-black uppercase tracking-widest animate-fade-in ${responseMsg.includes('✅') ? 'text-green-500' : 'text-red-500'}`}>
                  {responseMsg}
                </p>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-50 text-gray-400 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 transition-all"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading || uploading}
                className={`px-8 py-3 rounded-xl text-white font-black text-[10px] uppercase tracking-widest shadow-lg transition-all flex items-center gap-2 ${loading || uploading ? "bg-premium-teal/50 cursor-not-allowed" : "bg-premium-teal hover:shadow-premium-teal/20"
                  }`}
              >
                {loading ? (
                  <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : null}
                {loading ? "Syncing..." : initialData ? "Update Restaurant" : "Register Restaurant"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

